import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { analytics as analyticsApi } from '../api/client.js';
import type {
  AnalyticsSummary, AnalyticsEngagement, AnalyticsRetentionCohort,
  AnalyticsFeatureEngagement, AnalyticsSessions, AnalyticsDevices,
  AnalyticsRecentActivity,
} from '../api/client.js';
import { timeAgo, formatDate } from './users/shared.js';

const TABS = ['Summary', 'Engagement', 'Features', 'Sessions & Devices', 'Retention', 'Activity'] as const;
type Tab = typeof TABS[number];

interface Theme {
  bgSecondary: string; bgTertiary: string; bgInput: string; border: string; borderLight: string; borderMedium: string;
  text: string; textMuted: string; textFaint: string; textGhost: string; textTertiary: string; textSecondary: string;
  accent: string; accentBg: string; accentBorder: string;
  green: string; greenBg: string; red: string; redBg: string; orange: string; purple: string;
}

// Content-only — owns no page chrome, outer tab bar, or admin guard. Those
// live in the parent AdminPanel (mirrors Portal's AnalyticsTab.tsx, which is
// "purely a content-only component" per its own admin-panel container).
export default function AnalyticsPanel({ theme }: { theme: Theme }) {
  const [tab, setTab] = useState<Tab>('Summary');

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 14, borderBottom: `1px solid ${theme.border}`, marginBottom: 20 }}>
        {TABS.map((t) => (
          <span
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 0', fontSize: 12, textTransform: 'lowercase', whiteSpace: 'nowrap',
              borderBottom: tab === t ? `2px solid ${theme.accent}` : '2px solid transparent',
              color: tab === t ? theme.text : theme.textTertiary,
              cursor: 'pointer',
            }}
          >{t}</span>
        ))}
      </div>

      {/* Sub-tab content */}
      {tab === 'Summary' && <SummaryTab theme={theme} />}
      {tab === 'Engagement' && <EngagementTab theme={theme} />}
      {tab === 'Features' && <FeaturesTab theme={theme} />}
      {tab === 'Sessions & Devices' && <SessionsDevicesTab theme={theme} />}
      {tab === 'Retention' && <RetentionTab theme={theme} />}
      {tab === 'Activity' && <ActivityTab theme={theme} />}
    </div>
  );
}

// ─── Shared building blocks ──────────────────────────────────────

function Loading({ theme }: { theme: Theme }) {
  return <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted, fontSize: 13 }}>Loading...</div>;
}

function Empty({ theme, label }: { theme: Theme; label: string }) {
  return <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint, fontSize: 13 }}>{label}</div>;
}

function StatTile({ label, value, color, theme }: { label: string; value: string | number; color?: string; theme: Theme }) {
  const c = color || theme.accent;
  return (
    <div style={{
      flex: 1, padding: '14px 16px', borderRadius: 0,
      background: 'transparent', border: `1px solid ${theme.border}`, textAlign: 'center', minWidth: 100,
    }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: c }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.textFaint, marginTop: 2, textTransform: 'lowercase' }}>{label}</div>
    </div>
  );
}

// A simple horizontal bar row — no charting library, matches this app's
// zero-UI-dependency convention (see every other panel's plain inline styles).
function BarRow({ label, value, max, theme, color, sublabel }: { label: string; value: number; max: number; theme: Theme; color?: string; sublabel?: string }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  const c = color || theme.accent;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: theme.text, fontWeight: 500 }}>{label}{sublabel && <span style={{ color: theme.textFaint, fontWeight: 400 }}> · {sublabel}</span>}</span>
        <span style={{ color: theme.textMuted }}>{value.toLocaleString()}</span>
      </div>
      <div style={{ height: 6, borderRadius: 0, background: theme.bgInput, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: c, borderRadius: 0 }} />
      </div>
    </div>
  );
}

function SectionTitle({ children, theme }: { children: React.ReactNode; theme: Theme }) {
  return (
    <h4 style={{ fontSize: 11, fontWeight: 600, color: theme.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
      {children}
    </h4>
  );
}

// ─── Summary ──────────────────────────────────────────────────────

function SummaryTab({ theme }: { theme: Theme }) {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  useEffect(() => { analyticsApi.summary(30).then(setData).catch(console.error); }, []);
  if (!data) return <Loading theme={theme} />;

  const maxPage = Math.max(1, ...data.top_pages.map((p) => p.count));
  const maxUser = Math.max(1, ...data.top_users.map((u) => u.count));
  const maxDaily = Math.max(1, ...data.daily_activity.map((d) => d.count));

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
        <StatTile label="Total Events" value={data.totals.total_events} theme={theme} />
        <StatTile label="Unique Users" value={data.totals.unique_users} theme={theme} color={theme.green} />
        <StatTile label="Views" value={data.totals.views} theme={theme} color={theme.purple} />
        <StatTile label="Actions" value={data.totals.actions} theme={theme} color={theme.accent} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        <div>
          <SectionTitle theme={theme}>Top Pages / Features (30d)</SectionTitle>
          {data.top_pages.length === 0 ? <Empty theme={theme} label="No traffic yet" /> : data.top_pages.map((p) => (
            <BarRow key={p.page} label={p.feature || p.page} sublabel={p.page} value={p.count} max={maxPage} theme={theme} />
          ))}
        </div>
        <div>
          <SectionTitle theme={theme}>Top Users (30d)</SectionTitle>
          {data.top_users.length === 0 ? <Empty theme={theme} label="No activity yet" /> : data.top_users.map((u) => (
            <BarRow key={u.user_email} label={u.user_name || u.user_email} value={u.count} max={maxUser} theme={theme} color={theme.green} />
          ))}
        </div>
      </div>

      <div style={{ marginTop: 28 }}>
        <SectionTitle theme={theme}>Daily Activity</SectionTitle>
        {data.daily_activity.length === 0 ? <Empty theme={theme} label="No data yet" /> : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100 }}>
            {data.daily_activity.map((d) => (
              <div key={d.date} title={`${d.date}: ${d.count}`} style={{
                flex: 1, height: `${Math.max(4, (d.count / maxDaily) * 100)}%`,
                background: theme.accent, borderRadius: 0, minWidth: 4,
              }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Engagement ───────────────────────────────────────────────────

function EngagementTab({ theme }: { theme: Theme }) {
  const [data, setData] = useState<AnalyticsEngagement | null>(null);
  useEffect(() => { analyticsApi.engagement().then(setData).catch(console.error); }, []);
  if (!data) return <Loading theme={theme} />;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <StatTile label="Weekly Active Users" value={data.wau} theme={theme} />
        <StatTile label="Monthly Active Users" value={data.mau} theme={theme} color={theme.green} />
        <StatTile label="Stickiness (WAU/MAU)" value={`${Math.round(data.stickiness * 100)}%`} theme={theme} color={theme.accent} />
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <StatTile label="Avg Actions / User (30d)" value={data.avg_actions_per_user} theme={theme} color={theme.orange} />
        <StatTile label="New Users (30d)" value={data.new_users_30d} theme={theme} color={theme.purple} />
      </div>
      <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 20 }}>
        Stickiness is the share of monthly active users who were also active this week — a rough
        habit-strength signal. Higher is better.
      </div>
    </div>
  );
}

// ─── Features ─────────────────────────────────────────────────────

function FeaturesTab({ theme }: { theme: Theme }) {
  const [data, setData] = useState<AnalyticsFeatureEngagement[] | null>(null);
  useEffect(() => { analyticsApi.featureEngagement(30).then(setData).catch(console.error); }, []);
  if (!data) return <Loading theme={theme} />;
  if (data.length === 0) return <Empty theme={theme} label="No feature usage recorded yet" />;

  return (
    <div>
      {data.map((f) => (
        <div key={f.feature} style={{
          padding: 16, borderRadius: 0, background: 'transparent',
          border: `1px solid ${theme.border}`, marginBottom: 10,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{f.feature}</div>
            <div style={{ fontSize: 11, color: theme.textMuted }}>{f.distinct_doers} distinct doer{f.distinct_doers !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: 20, marginBottom: f.top_actions.length ? 10 : 0 }}>
            <div><span style={{ fontSize: 18, fontWeight: 700, color: theme.purple }}>{f.views}</span> <span style={{ fontSize: 11, color: theme.textFaint }}>views</span></div>
            <div><span style={{ fontSize: 18, fontWeight: 700, color: theme.accent }}>{f.actions}</span> <span style={{ fontSize: 11, color: theme.textFaint }}>actions</span></div>
          </div>
          {f.top_actions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {f.top_actions.map((a) => (
                <span key={a.page} style={{
                  fontSize: 10, color: theme.textMuted, background: theme.bgInput,
                  padding: '3px 8px', borderRadius: 0,
                }}>{a.page} &middot; {a.count}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Sessions & Devices ───────────────────────────────────────────

function SessionsDevicesTab({ theme }: { theme: Theme }) {
  const [sessions, setSessions] = useState<AnalyticsSessions | null>(null);
  const [devices, setDevices] = useState<AnalyticsDevices | null>(null);
  useEffect(() => {
    analyticsApi.sessions(7).then(setSessions).catch(console.error);
    analyticsApi.devices(30).then(setDevices).catch(console.error);
  }, []);

  const fmtMs = (ms: number) => {
    const mins = Math.round(ms / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    return `${(mins / 60).toFixed(1)}h`;
  };

  return (
    <div>
      <SectionTitle theme={theme}>Sessions (last 7 days, 30-min inactivity gap)</SectionTitle>
      {!sessions ? <Loading theme={theme} /> : (
        <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
          <StatTile label="Total Sessions" value={sessions.total_sessions} theme={theme} />
          <StatTile label="Avg Duration" value={fmtMs(sessions.avg_duration_ms)} theme={theme} color={theme.green} />
          <StatTile label="Median Duration" value={fmtMs(sessions.median_duration_ms)} theme={theme} color={theme.accent} />
        </div>
      )}

      <SectionTitle theme={theme}>Devices (last 30 days)</SectionTitle>
      {!devices ? <Loading theme={theme} /> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24 }}>
          <DeviceBreakdownColumn title="Browser" data={devices.browsers} theme={theme} />
          <DeviceBreakdownColumn title="OS" data={devices.os} theme={theme} />
          <DeviceBreakdownColumn title="Device Type" data={devices.device_types} theme={theme} />
        </div>
      )}
    </div>
  );
}

function DeviceBreakdownColumn({ title, data, theme }: { title: string; data: Record<string, number>; theme: Theme }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, marginBottom: 8 }}>{title}</div>
      {entries.length === 0 ? <div style={{ fontSize: 11, color: theme.textFaint }}>No data</div> : entries.map(([name, count]) => (
        <BarRow key={name} label={name} value={count} max={max} theme={theme} />
      ))}
    </div>
  );
}

// ─── Retention ────────────────────────────────────────────────────

function RetentionTab({ theme }: { theme: Theme }) {
  const [data, setData] = useState<AnalyticsRetentionCohort[] | null>(null);
  useEffect(() => { analyticsApi.retention(8).then(setData).catch(console.error); }, []);
  if (!data) return <Loading theme={theme} />;
  if (data.length === 0) return <Empty theme={theme} label="Not enough history yet for retention cohorts" />;

  return (
    <div>
      <div style={{ fontSize: 11, color: theme.textFaint, marginBottom: 16 }}>
        Each row is a cohort of users grouped by the week they first showed up. "Retained" = came
        back at least once 7-13 days later.
      </div>
      {data.map((c) => (
        <div key={c.cohort_week} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: theme.text, fontWeight: 500 }}>Week {String(c.cohort_week).slice(-2)}</span>
            <span style={{ color: theme.textMuted }}>{c.retained}/{c.cohort_size} retained &middot; {Math.round(c.retention_rate * 100)}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 0, background: theme.bgInput, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.round(c.retention_rate * 100)}%`, background: theme.green, borderRadius: 0 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Activity ─────────────────────────────────────────────────────

function ActivityTab({ theme }: { theme: Theme }) {
  const [data, setData] = useState<AnalyticsRecentActivity | null>(null);
  const [page, setPage] = useState(1);
  useEffect(() => { analyticsApi.recentActivity({ page, limit: 50 }).then(setData).catch(console.error); }, [page]);
  if (!data) return <Loading theme={theme} />;
  if (data.activities.length === 0) return <Empty theme={theme} label="No activity recorded yet" />;

  return (
    <div>
      {data.activities.map((a) => (
        <div key={a.id} style={{
          padding: '10px 0', borderBottom: `1px solid ${theme.borderLight}`,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%', marginTop: 6, flexShrink: 0,
            background: a.event_type === 'feature_action' ? theme.accent : theme.purple,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: theme.text, fontWeight: 500 }}>
              {a.user_name || a.user_email} &middot; {a.feature || a.page}
              <span style={{ color: theme.textFaint, fontWeight: 400 }}> ({a.event_type === 'feature_action' ? 'action' : 'view'})</span>
            </div>
            <div style={{ fontSize: 10, color: theme.textGhost, marginTop: 4 }}>{timeAgo(a.created_at)} &middot; {formatDate(a.created_at)}</div>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{
            padding: '6px 14px', fontSize: 12, textTransform: 'lowercase',
            background: 'none', border: `1px solid ${theme.borderMedium}`,
            borderRadius: 0, color: page <= 1 ? theme.textFaint : theme.textSecondary,
            cursor: page <= 1 ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}
        >previous</button>
        <span style={{ fontSize: 11, color: theme.textMuted, alignSelf: 'center' }}>
          page {data.pagination.page} of {data.pagination.pages || 1}
        </span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={page >= data.pagination.pages}
          style={{
            padding: '6px 14px', fontSize: 12, textTransform: 'lowercase',
            background: 'none', border: `1px solid ${theme.borderMedium}`,
            borderRadius: 0, color: page >= data.pagination.pages ? theme.textFaint : theme.textSecondary,
            cursor: page >= data.pagination.pages ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}
        >next</button>
      </div>
    </div>
  );
}
